/**
 * Navigation Service Module
 * Provides methods for navigating between pages with:
 * - Routing logic for test, import, and login pages
 * - Error handling for navigation failures
 * - Test context management
 */

import { isTestContext } from '../types/mainPage.types';

/**
 * Custom error class for navigation-related errors
 */
class NavigationError extends Error {
  constructor(message, type = 'unknown', originalError = null) {
    super(message);
    this.name = 'NavigationError';
    this.type = type; // 'invalid_context', 'invalid_route', 'navigation_failed'
    this.originalError = originalError;
    this.timestamp = new Date();
  }
}

/**
 * Navigation Service class
 * Manages routing between pages in the application
 */
class NavigationService {
  constructor() {
    this.navigate = null;
    this.errorHandlers = [];
  }

  /**
   * Initialize the navigation service with a navigate function
   * This should be called from a component that has access to useNavigate()
   * @param {Function} navigateFunction - The navigate function from react-router-dom
   */
  initialize(navigateFunction) {
    if (typeof navigateFunction !== 'function') {
      throw new NavigationError(
        'Navigate function must be a function',
        'invalid_route'
      );
    }
    this.navigate = navigateFunction;
  }

  /**
   * Check if the service is initialized
   * @returns {boolean} True if initialized
   */
  isInitialized() {
    return this.navigate !== null;
  }

  /**
   * Register an error handler
   * @param {Function} handler - Error handler function that receives NavigationError
   */
  onNavigationError(handler) {
    if (typeof handler !== 'function') {
      throw new Error('Error handler must be a function');
    }
    this.errorHandlers.push(handler);
  }

  /**
   * Trigger all registered error handlers
   * @param {NavigationError} error - The navigation error
   * @private
   */
  _triggerErrorHandlers(error) {
    this.errorHandlers.forEach(handler => {
      try {
        handler(error);
      } catch (err) {
        console.error('Error in navigation error handler:', err);
      }
    });
  }

  /**
   * Navigate to the test page with the given test context
   * @param {TestContext} context - Test context containing type, noteId, and flashcards
   * @throws {NavigationError} On validation or navigation failure
   * 
   * Requirements: 2.2, 3.4
   */
  navigateToTest(context) {
    try {
      // Validate service is initialized
      if (!this.isInitialized()) {
        throw new NavigationError(
          'Navigation service not initialized. Call initialize() first.',
          'navigation_failed'
        );
      }

      // Validate flashcards array is not empty first (before type guard)
      if (!context || !context.flashcards || context.flashcards.length === 0) {
        throw new NavigationError(
          'Cannot start test with no flashcards',
          'invalid_context'
        );
      }

      // Validate test context with type guard
      if (!isTestContext(context)) {
        throw new NavigationError(
          'Invalid test context. Must include type, flashcards, and noteId (for note type).',
          'invalid_context'
        );
      }

      // Navigate to practice/test page with state
      this.navigate('/practice', { 
        state: { 
          testContext: context 
        } 
      });
    } catch (error) {
      // If it's already a NavigationError, re-throw it
      if (error instanceof NavigationError) {
        this._triggerErrorHandlers(error);
        throw error;
      }

      // Wrap other errors
      const navError = new NavigationError(
        `Navigation to test failed: ${error.message}`,
        'navigation_failed',
        error
      );
      this._triggerErrorHandlers(navError);
      throw navError;
    }
  }

  /**
   * Navigate to the import page
   * @throws {NavigationError} On navigation failure
   * 
   * Requirements: 4.2
   */
  navigateToImport() {
    try {
      // Validate service is initialized
      if (!this.isInitialized()) {
        throw new NavigationError(
          'Navigation service not initialized. Call initialize() first.',
          'navigation_failed'
        );
      }

      // Navigate to import page
      this.navigate('/import');
    } catch (error) {
      // If it's already a NavigationError, re-throw it
      if (error instanceof NavigationError) {
        this._triggerErrorHandlers(error);
        throw error;
      }

      // Wrap other errors
      const navError = new NavigationError(
        `Navigation to import failed: ${error.message}`,
        'navigation_failed',
        error
      );
      this._triggerErrorHandlers(navError);
      throw navError;
    }
  }

  /**
   * Navigate to the login page
   * @throws {NavigationError} On navigation failure
   * 
   * Requirements: 5.3
   */
  navigateToLogin() {
    try {
      // Validate service is initialized
      if (!this.isInitialized()) {
        throw new NavigationError(
          'Navigation service not initialized. Call initialize() first.',
          'navigation_failed'
        );
      }

      // Navigate to login page
      this.navigate('/login');
    } catch (error) {
      // If it's already a NavigationError, re-throw it
      if (error instanceof NavigationError) {
        this._triggerErrorHandlers(error);
        throw error;
      }

      // Wrap other errors
      const navError = new NavigationError(
        `Navigation to login failed: ${error.message}`,
        'navigation_failed',
        error
      );
      this._triggerErrorHandlers(navError);
      throw navError;
    }
  }

  /**
   * Clear all error handlers
   * Useful for cleanup
   */
  clearErrorHandlers() {
    this.errorHandlers = [];
  }
}

// Export singleton instance
const navigationService = new NavigationService();

export default navigationService;
export { NavigationService, NavigationError };
